import { components } from "@/api-schema";
import { useCallback, useMemo, useState } from "react";

type GroupWithAppRole = components["schemas"]["GroupWithAppRoleResponseDto"];

interface AppRoleGroupSelection {
  allSelectedGroups: GroupWithAppRole[];
  currentPageSelectedGroups: GroupWithAppRole[];
  groupUuidsToAdd: string[];
  groupUuidsToRemove: string[];
  handleSelectionChange: (selected: GroupWithAppRole[] | string[]) => void;
  handleClearSelection: () => void;
  handleRowClick: (clickedGroup: GroupWithAppRole) => void;
}

/**
 * Manages cross-page group selection state for app roles and derives the delta
 * (groupUuidsToAdd/groupUuidsToRemove) needed to detect unsaved changes.
 *
 * When a page loads, rows with hasAppRole=true are auto-selected and their UUIDs
 * are recorded as the baseline. Changes from that baseline produce the delta.
 */
export function useAppRoleGroupSelection(currentPageData: GroupWithAppRole[] | undefined): AppRoleGroupSelection {
  const [allSelectedGroups, setAllSelectedGroups] = useState<GroupWithAppRole[]>([]);
  const [initialGroupUuids, setInitialGroupUuids] = useState<string[]>([]);

  const [seededPageData, setSeededPageData] = useState<GroupWithAppRole[] | undefined>(undefined);

  // Seed selections for each newly loaded page during render ("adjust state when a prop changes" pattern).
  if (currentPageData !== seededPageData) {
    setSeededPageData(currentPageData);

    if (currentPageData) {
      setInitialGroupUuids((previousUuids) => {
        const newUuids = currentPageData
          .filter((group) => group.hasAppRole)
          .map((group) => group.uuid)
          .filter((uuid) => !previousUuids.includes(uuid));
        return newUuids.length > 0 ? [...previousUuids, ...newUuids] : previousUuids;
      });

      setAllSelectedGroups((previouslySelected) => {
        const newMembers = currentPageData
          .filter((group) => group.hasAppRole)
          .filter((group) => !previouslySelected.some((existing) => existing.uuid === group.uuid));
        return newMembers.length > 0 ? [...previouslySelected, ...newMembers] : previouslySelected;
      });
    }
  }

  const currentPageSelectedGroups = useMemo(
    () =>
      allSelectedGroups.filter((selectedGroup) =>
        currentPageData?.some((pageGroup) => pageGroup.uuid === selectedGroup.uuid),
      ),
    [allSelectedGroups, currentPageData],
  );

  const groupUuidsToAdd = useMemo(
    () => allSelectedGroups.filter((group) => !initialGroupUuids.includes(group.uuid)).map((group) => group.uuid),
    [allSelectedGroups, initialGroupUuids],
  );

  const groupUuidsToRemove = useMemo(
    () =>
      initialGroupUuids.filter(
        (groupUuid) => !allSelectedGroups.some((selectedGroup) => selectedGroup.uuid === groupUuid),
      ),
    [allSelectedGroups, initialGroupUuids],
  );

  const handleSelectionChange = useCallback(
    (selected: GroupWithAppRole[] | string[]) => {
      const newSelection = selected as GroupWithAppRole[];

      setAllSelectedGroups((previouslySelected) => {
        const withoutCurrentPage = previouslySelected.filter(
          (selectedGroup) => !currentPageData?.some((pageGroup) => pageGroup.uuid === selectedGroup.uuid),
        );
        return [...withoutCurrentPage, ...newSelection];
      });
    },
    [currentPageData],
  );

  const handleClearSelection = useCallback(() => setAllSelectedGroups([]), []);

  const handleRowClick = useCallback((clickedGroup: GroupWithAppRole) => {
    setAllSelectedGroups((previouslySelected) => {
      const isSelected = previouslySelected.some((selectedGroup) => selectedGroup.uuid === clickedGroup.uuid);
      return isSelected
        ? previouslySelected.filter((selectedGroup) => selectedGroup.uuid !== clickedGroup.uuid)
        : [...previouslySelected, clickedGroup];
    });
  }, []);

  return {
    allSelectedGroups,
    currentPageSelectedGroups,
    groupUuidsToAdd,
    groupUuidsToRemove,
    handleSelectionChange,
    handleClearSelection,
    handleRowClick,
  };
}
